/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  Divider,
  Checkbox,
  TextField,
  Typography,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';

import { API } from '../../../../api';
import { toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import AppLoader from '../../../../components/AppLoader';
import ElliottWorking from '../../../../components/ui/ElliottWorking';
import { getUserDetails } from '../../../../utils/orgName';
import ChatAvatarComponent from '../chat/ChatAvatarComponent';
import { isMemberRole, MemberRole, useMemberWord } from '../../../../utils/memberTypeLabels';
import { matchesActions } from '../../../../redux/slices/getAllMatches/getAllMatchesSlice';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import MenteeProfileModal from './MentorDash/Components/MenteeProfileModal';
import { ALWAYS_ON_AI_FIELD_LABEL } from '../Matches/aiMatchingFields';

interface AIFieldComparison {
  field: string;
  reasoning: string;
}

interface Mentor {
  mid: string;
  displayName: string;
  headshot: string;
  position?: string;
}

interface RecruitNewMentorModalProps {
  title?: string;
  open: boolean;
  onClose: () => void;
  mentorsList: Mentor[];
  onProfileClick: (mid: string) => void;
  onChatClick: (mentor: Mentor) => void;
  onCalendarClick: (mentor: Mentor) => void;
  objId: string;
  grpId: string;
  apiType?: any;
  isAssignPopup?: any;
  onSelect?: any;
  assigneeRole?: any;
  userId?: any;
  dataArray?: any[];
  setDataArray?: any;
  learningObjective?: any;
  origin?: string;
}

const RecruitNewMentor: React.FC<RecruitNewMentorModalProps> = (props) => {
  const {
    title,
    open,
    onClose,
    mentorsList,
    onProfileClick,
    onChatClick,
    onCalendarClick,
    objId,
    grpId,
    apiType,
    isAssignPopup,
    userId,
    assigneeRole,
    onSelect,
    dataArray,
    setDataArray,
    learningObjective,
    origin
  } = props;

  // --------------------------
  // MESMA LÓGICA ORIGINAL
  // --------------------------
  const [data, setData] = useState<any[]>(mentorsList || []);
  const [recommendedData, setRecommendedData] = useState<any[]>([]);
  const [suggestedData, setSuggestedData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedObjectives, setSelectedObjectives] = useState<Record<string, string[]>>({});
  const [searchValue, setSearchValue] = useState('');
  const [selectedFromSearch, setSelectedFromSearch] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileUser, setProfileUser] = useState<any>(null);
  const handleOpenProfile = (mentor: any) => {
    setProfileUser(mentor);
    setProfileModalOpen(true);
  };
  const handleCloseProfile = () => {
    setProfileModalOpen(false);
    setProfileUser(null);
  };


  const dispatch = useAppDispatch();
  const currentUser = getUserDetails();
  const { orgId } = currentUser;

  const aiMatchingSettings = useAppSelector((state) => state.groupDetails.data?.matchesSettings?.aiMatching);
  // AI Matching is an admin tool: only the admin Matches page (origin 'admin')
  // gets the AI Match tab. Members opening this modal from their own home see
  // the regular recommendations only, whatever the program setting says.
  const isAiMatchingEnabled = aiMatchingSettings?.enabled === true && origin === 'admin';

  const [activeTab, setActiveTab] = useState<'recommendations' | 'ai'>('recommendations');
  const [aiRecommendations, setAiRecommendations] = useState<any[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiObjectivesHardFilter, setAiObjectivesHardFilter] = useState(false);
  const [aiEmptyMessage, setAiEmptyMessage] = useState<string | null>(null);
  const [aiErrorMessage, setAiErrorMessage] = useState<string | null>(null);
  // AI Match cards start collapsed; this tracks which ones the admin has expanded.
  const [expandedAiCards, setExpandedAiCards] = useState<Record<string, boolean>>({});

  const assigneeIdForAI: string = userId || currentUser.userId || '';
  const assigneeRoleForAI: 'Mentee' | 'Mentor' = isAssignPopup
    ? (assigneeRole === 'Assign Mentor' ? 'Mentee' : 'Mentor')
    : apiType === 'mentee'
      ? 'Mentor'
      : 'Mentee';

  const w = useMemberWord();
  const mentorRoleText = w('mentor', { lower: true });
  const menteeRoleText = w('mentee', { lower: true });
  const mentorPlural = w('mentor', { plural: true, lower: true });
  const menteePlural = w('mentee', { plural: true, lower: true });

  const filterData = (data: any[], searchValue: string) =>
    data.filter((item) => item?.name?.toLowerCase().includes(searchValue.toLowerCase()));

  const filteredRecommended = filterData(recommendedData, searchValue);
  const filteredSuggested = filterData(suggestedData, searchValue);

  const getHeadingText = () => {
    if (isAssignPopup) return '';
    if (apiType === 'all') return `Find ${w('mentor', { article: true })}`;
    if (apiType === 'mentee') return `Find ${w('mentee', { article: true })}`;
    return `Find ${w('mentor', { article: true })}`;
  };

  const getListTitle = () => {
    if (apiType === 'mentee') return `Find ${menteePlural} who need your expertise.`;
    return `Find ${mentorPlural} with the relevant expertise.`;
  };

  const getTopSectionTitle = () => {
    if (apiType === 'mentee') return 'Areas of Expertise';
    return 'Learning Objectives';
  };

  const getNoDataMessage = () =>
    isAssignPopup
      ? assigneeRole === 'Assign Mentor'
        ? `No relevant ${mentorPlural} available.`
        : `No relevant ${menteePlural} available.`
      : 'No relevant people available.';

  const initialMatchingFields = {
    gender: false,
    firstGenStudent: false,
    ethnicity: false,
    disability: false,
    hobbies: false,
    major: false,
    university: false,
    industry: false,
    title: false,
    topics: false,
    industries: false,
    objectives: true
  };

  // === GETTERS =========================================
  const getMentors = async () => {
    setLoading(true);
    try {
      let response;
      if (apiType === 'objective' && objId) {
        response = await API.getObjectiveMentors({ objId, grpId, orgId });
      } else if (apiType === 'mentee') {
        response = await API.getAllObjectiveMentees({ grpId, orgId });
      } else {
        response = await API.getAllObjectiveMentors({ orgId, grpId });
      }
      if (response?.status === 200) {
        setData(response?.data?.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const getRecommendedMentors = async () => {
    setLoading(true);
    try {
      if (apiType === 'match') {
        const response = await API.getListToAssignMatch({
          orgId,
          userId,
          grpId,
          data: {
            assigneeRole: assigneeRole === 'Assign Mentor' ? 'Mentee' : 'Mentor',
            matchingFields: initialMatchingFields
          },
          isMatchingFields: true
        });

        if (response.status === 200) {
          const result = response?.data?.data;
          if (result?.recommendations || result?.suggestions) {
            const suggestions = (result.suggestions || []).filter((s: any) => s.matchedObjectives?.length > 0);
            setRecommendedData(result.recommendations || []);
            setSuggestedData(suggestions);
            setData([]);
          } else {
            setData(result || []);
            setRecommendedData([]);
            setSuggestedData([]);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAISuggestions = async (overrides?: { objectivesHardFilter?: boolean }) => {
    if (!assigneeIdForAI || !grpId) {
      setAiErrorMessage('Unable to load suggestions from Elliott: missing user or program info.');
      return;
    }
    const hardFilter = overrides?.objectivesHardFilter ?? aiObjectivesHardFilter;
    setAiLoading(true);
    setAiErrorMessage(null);
    setAiEmptyMessage(null);
    try {
      const response = await API.getAIMatchSuggestions({
        orgId,
        assigneeId: assigneeIdForAI,
        grpId,
        assigneeRole: assigneeRoleForAI,
        objectivesHardFilter: hardFilter,
        location: currentUser.location
      });
      const payload = response?.data;
      if (response?.status === 200 && payload?.data) {
        // Always show highest match % first, regardless of the AI's holistic ordering.
        const recs: any[] = [...(payload.data?.recommendations || [])].sort(
          (a: any, b: any) => (b?.aiScore ?? -1) - (a?.aiScore ?? -1)
        );
        setAiRecommendations(recs);
        setExpandedAiCards({});
        if (recs.length === 0) {
          setAiEmptyMessage(
            hardFilter
              ? 'No suggestions from Elliott match the hard objectives filter. Try turning it off to broaden the pool.'
              : 'No suggestions from Elliott are available right now.'
          );
        }
      } else {
        setAiErrorMessage(payload?.message || 'Suggestions from Elliott could not be loaded. Try again.');
        setAiRecommendations([]);
      }
    } catch (err: any) {
      setAiErrorMessage(
        err?.response?.data?.message || err?.message || 'Suggestions from Elliott could not be loaded. Try again.'
      );
      setAiRecommendations([]);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setData([]);
      setRecommendedData([]);
      setSuggestedData([]);
      setSelectedIds([]);
      setSelectedObjectives({});
      setActiveTab('recommendations');
      setAiRecommendations([]);
      setAiObjectivesHardFilter(false);
      setAiEmptyMessage(null);
      setAiErrorMessage(null);
      if (apiType === 'match') getRecommendedMentors();
      else getMentors();
    } else {
      setLoading(true);
      setData(mentorsList || []);
      setRecommendedData([]);
      setSuggestedData([]);
    }
  }, [open]);

  useEffect(() => {
    if (open && activeTab === 'ai' && aiRecommendations.length === 0 && !aiLoading && !aiErrorMessage) {
      fetchAISuggestions();
    }
  }, [open, activeTab]);

  const handleClose = () => {
    setData([]);
    setRecommendedData([]);
    setSuggestedData([]);
    setSelectedIds([]);
    setSelectedObjectives({});
    setSearchValue('');
    setSelectedFromSearch(false);
    setLoading(false);
    setActiveTab('recommendations');
    setAiRecommendations([]);
    setAiObjectivesHardFilter(false);
    setAiEmptyMessage(null);
    setAiErrorMessage(null);
    onClose();
  };

  const handleAIMemberSelect = (mentor: any) => {
    const id = assigneeRoleForAI === 'Mentee' ? mentor.mentorId : mentor.menteeId;
    if (!id) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const handleSearchChange = (e: any) => setSearchValue(e.target.value);

  const handleMemberSelect = (mentor: any) => {
    const id = assigneeRole === 'Assign Mentor' ? mentor.mentorId : mentor.menteeId;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const handleObjectiveSelect = (event: any, id: string, objectiveId: string, mentor: any) => {
    setSelectedObjectives((prev) => {
      const current = prev[id] || [];
      const newSelection = event.target.checked ? [...current, objectiveId] : current.filter((o) => o !== objectiveId);

      setSelectedIds((prevIds) => {
        if (newSelection.length > 0 && !prevIds.includes(id)) {
          return [...prevIds, id];
        } else if (newSelection.length === 0 && prevIds.includes(id)) {
          return prevIds.filter((p) => p !== id);
        }
        return prevIds;
      });

      return { ...prev, [id]: newSelection };
    });
  };

  const handleSelect = async () => {
    if (selectedIds.length === 0) {
      toast.error(`Please select at least one ${assigneeRole === 'Assign Mentor' ? mentorRoleText : menteeRoleText}`);
      return;
    }

    try {
      setLoading(true);

      const payload = selectedIds.map((id) => ({
        assigneeRole: assigneeRole === 'Assign Mentor' ? 'Mentee' : 'Mentor',
        mentorId: assigneeRole === 'Assign Mentor' ? id : userId,
        menteeId: assigneeRole === 'Assign Mentor' ? userId : id,
        objectiveIds: selectedFromSearch ? [] : selectedObjectives[id] || []
      }));

      const response = await API.assignAMatch({ orgId, grpId, data: payload });

      if (response?.status === 200) {
        const matchesData = Array.isArray(response.data?.data) ? response.data.data : [response.data?.data];

        if (matchesData.length > 0) {
          setDataArray((prev: any) => [...prev, ...matchesData]);

          matchesData.forEach((match: any) => {
            if (match.mentee?.userId && match.mentor?.userId) {
              dispatch(
                matchesActions.updateMenteesMentors({
                  menteeId: match.mentee.userId,
                  mentorId: match.mentor.userId
                })
              );
            }
          });
        }

        if (origin === 'admin' && matchesData.length > 0) {
          await confirmMatchesImmediately(matchesData);
        }

        toast.success(`${assigneeRole === 'Assign Mentor' ? w('mentor') : w('mentee')} assigned successfully`);
        onClose();
        if (onSelect) onSelect(selectedIds);
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmMatchesImmediately = async (matches: any[]) => {
    const ids = matches
      .map((match) => match?.id || match?._id || match?.data?.id || match?.data?._id)
      .filter(Boolean);
    if (!ids.length) return;

    try {
      await Promise.all(
        ids.map((id) =>
          API.confirmMatch({
            orgId,
            grpId,
            data: { id }
          })
        )
      );

      setDataArray((prev: any[]) =>
        prev.map((item) => {
          const itemId = item?.id || item?._id || item?.data?.id || item?.data?._id;
          if (itemId && ids.includes(itemId)) {
            return { ...item, isConfirmed: true };
          }
          return item;
        })
      );

      ids.forEach((id) => dispatch(matchesActions.updateConfirmStatus(id)));
    } catch {
      toast.error('The match could not be confirmed. Try again.');
    }
  };

  const combinedRecommended = filteredRecommended.length ? filteredRecommended : [];
  const combinedSuggested = filteredSuggested.length ? filteredSuggested : [];
  const getLearningObjectiveId = (obj: any) =>
    obj != null ? String(obj?.id ?? obj?._id ?? obj?.objectiveId ?? obj?.objective ?? obj?.name ?? '') : '';

  const combinedList = () => {
    let list =
      combinedRecommended.length || combinedSuggested.length
        ? [...combinedRecommended, ...combinedSuggested]
        : data || [];
    if (learningObjective) {
      const targetId = getLearningObjectiveId(learningObjective);
      if (!targetId) return list;
      return list.filter((item) =>
        item.matchedObjectives?.some((obj: any) => getLearningObjectiveId(obj) === targetId)
      );
    } else return list;
  };

  const getObjectiveTitle = () =>
    combinedList()[0]?.matchedObjectives?.[0]?.name ||
    combinedList()[0]?.objectives?.[0]?.name ||
    combinedList()[0]?.objective ||
    combinedList()[0]?.objectiveName ||
    'Learning Objective';

  // ======================================================================
  //                     UI — F I G M A   R E P L I C A T E D
  // ======================================================================

  const renderObjectiveChips = (mentor: any) => {
    const objectives = mentor?.matchedObjectives || mentor?.objectives || [];
    if (!objectives.length) return null;

    const checkboxId = assigneeRole === 'Assign Mentor' ? mentor.mentorId : mentor.menteeId;

    return (
      <Box
        sx={{
          pl: '60px !important',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '5px',
          mt: '4px'
        }}
      >
        {objectives.map((obj: any, idx: number) =>
          isAssignPopup ? (
            <FormControlLabel
              key={idx}
              control={
                <Checkbox
                  checked={selectedObjectives[checkboxId]?.includes(obj.id) || false}
                  onChange={(e) => handleObjectiveSelect(e, checkboxId, obj.id, mentor)}
                  sx={{
                    color: '#FDFDFD !important'
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: '12px !important',
                    color: '#FDFDFD !important'
                  }}
                >
                  {obj.name}
                </Typography>
              }
              sx={{
                border: '1px solid #444444 !important',
                borderRadius: '20px !important',
                px: '8px !important',
                py: '2px !important',
                m: '0 !important'
              }}
            />
          ) : (
            <Chip
              key={idx}
              label={obj.name}
              sx={{
                border: '1px solid #444444 !important',
                borderRadius: '20px !important',
                background: 'transparent !important',
                color: '#FDFDFD !important',
                fontSize: '12px !important',
                height: '26px !important'
              }}
            />
          )
        )}
      </Box>
    );
  };

  const renderObjectiveCheckbox = (mentor: any) => {
    const objectives = mentor?.matchedObjectives || mentor?.objectives || [];
    if (!objectives.length) return null;

    const checkboxId = assigneeRole === 'Assign Mentor' ? mentor.mentorId : mentor.menteeId;

    return (
      <div
        style={{
          marginLeft: '60px'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            mt: '4px'
          }}
        >
          {objectives.map((obj: any, idx: number) =>
            isAssignPopup ? (
              <FormControlLabel
                key={idx}
                control={
                  <Checkbox
                    checked={selectedObjectives[checkboxId]?.includes(obj.id) || false}
                    onChange={(e) => handleObjectiveSelect(e, checkboxId, obj.id, mentor)}
                    sx={{
                      color: '#FDFDFD !important'
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '12px !important',
                      color: '#FDFDFD !important'
                    }}
                  >
                    {obj.name}
                  </Typography>
                }
                // sx={{
                //   border: '1px solid #444444 !important',
                //   borderRadius: '20px !important',
                //   px: '8px !important',
                //   py: '2px !important',
                //   m: '0 !important'
                // }}
              />
            ) : (
              <Chip
                key={idx}
                label={obj.name}
                sx={{
                  border: '1px solid #444444 !important',
                  borderRadius: '20px !important',
                  background: 'transparent !important',
                  color: '#FDFDFD !important',
                  fontSize: '12px !important',
                  height: '26px !important'
                }}
              />
            )
          )}
        </Box>
      </div>
    );
  };

  const renderMentorRow = (mentor: any, index: number) => {
    const id = assigneeRole === 'Assign Mentor' ? mentor.mentorId : mentor.menteeId;

    const isSelected = selectedIds.includes(id);
    const avatar = mentor?.image || mentor?.headshot;
    const name = mentor?.name || mentor?.displayName || w('mentor');
    const titleText = mentor?.title || mentor?.position || mentor?.headline || mentor?.role || '';
    const rowRole =
      apiType === 'mentor' || apiType === 'mentee'
        ? apiType
        : mentor?.role || (assigneeRole === 'Assign Mentor' ? 'mentor' : 'mentee');
    const rowRoleLabel = isMemberRole(rowRole) ? w(rowRole.trim().toLowerCase() as MemberRole) : rowRole;

    return (
      <Box key={index} sx={{ width: '100%' }}>
        {index > 0 && <Divider sx={{ borderColor: '#2A2A2A !important', my: '20px' }} />}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Avatar + info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
              <Box onClick={() => onProfileClick(mentor?.userId || mentor?.mid || '')} sx={{ cursor: 'pointer' }}>
                <ChatAvatarComponent
                  width="50px"
                  height="50px"
                  type="noStatus"
                  image={avatar}
                  firstLetter={name.slice(0, 1)}
                />
              </Box>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: '700 !important',
                    color: '#FDFDFD !important'
                  }}
                >
                  {name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: 0.5 }}>
                  <Chip
                    label={rowRoleLabel}
                    size="small"
                    sx={{
                      border: '1px solid #444444 !important',
                      borderRadius: '20px !important',
                      background: 'transparent !important',
                      color: '#FDFDFD !important',
                      fontSize: '11px !important',
                      height: '22px !important'
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '12px !important',
                      color: '#B6B6B6 !important',
                      fontWeight: '500 !important'
                    }}
                  >
                    Matches: {mentor?.matchCount ?? 0}
                  </Typography>
                </Box>
                {titleText && (
                  <Typography
                    sx={{
                      fontSize: '12px !important',
                      color: '#B6B6B6 !important',
                      fontWeight: '500 !important'
                    }}
                  >
                    {titleText}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Right buttons */}
            {isAssignPopup ? (
              <div>
                <IconButton
                  onClick={() => handleOpenProfile(mentor)}
                  sx={{
                    width: '40px !important',
                    height: '40px !important',
                    background: '#CDCDCD !important',
                    borderRadius: '999px !important',
                    border: '1px solid #2A2A2A !important'
                  }}
                >
                  <AccountCircleOutlinedIcon sx={{ color: 'black' }} />
                </IconButton>
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleMemberSelect(mentor)}
                  sx={{ color: '#FDFDFD !important', display: 'none' }}
                />
              </div>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <IconButton
                  onClick={() => onChatClick(mentor)}
                  sx={{
                    width: '40px !important',
                    height: '40px !important',
                    background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%) !important',
                    borderRadius: '999px !important'
                  }}
                >
                  <QuestionAnswerIcon sx={{ color: 'white' }} />
                </IconButton>

                <IconButton
                  onClick={() => handleOpenProfile(mentor)}
                  sx={{
                    width: '40px !important',
                    height: '40px !important',
                    background: '#CDCDCD !important',
                    borderRadius: '999px !important',
                    border: '1px solid #2A2A2A !important'
                  }}
                >
                  <AccountCircleOutlinedIcon sx={{ color: 'black' }} />
                </IconButton>
              </Box>
            )}
          </Box>

          {origin !== 'admin' ? renderObjectiveChips(mentor) : renderObjectiveCheckbox(mentor)}
          {/* {renderObjectiveChips(mentor)} */}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        fullWidth
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: '550px !important',
            maxWidth: '550px !important',
            background: '#0F0F0F !important',
            borderRadius: '8px !important',
            outline: '1px solid #444444 !important',
            outlineOffset: '-1px !important',
            overflow: 'hidden !important'
          }
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            width: '100%',
            px: '25px !important',
            py: '20px !important',
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #2A2A2A !important',
            position: 'relative'
          }}
        >
          <Typography
            sx={{
              flex: 1,
              color: '#FDFDFD !important',
              fontSize: '18px !important',
              fontWeight: '700 !important'
            }}
          >
            {origin === 'admin' ? 'Assign Match(es)' : getHeadingText()}
          </Typography>

          {/* Close icon */}
          <IconButton
            onClick={handleClose}
            sx={{
              width: '22px !important',
              height: '22px !important',
              padding: '6px !important',
              position: 'absolute',
              right: '25px',
              top: '20px'
            }}
          >
            <CloseIcon sx={{color: 'white'}}/>
          </IconButton>
        </Box>

        {/* CONTENT */}
        <DialogContent
          sx={{
            p: '25px !important',
            display: 'flex',
            flexDirection: 'column',
            gap: '30px'
          }}
        >
          {/* TITLE BLOCK */}
          {origin !== 'admin' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Typography
                sx={{
                  fontSize: '14px !important',
                  color: '#FDFDFD !important',
                  fontWeight: '400 !important'
                }}
              >
                {getTopSectionTitle()}
              </Typography>

              <Typography
                sx={{
                  fontSize: '16px !important',
                  color: '#F79420 !important',
                  fontWeight: '700 !important',
                  textTransform: 'capitalize'
                }}
              >
                {learningObjective?.objective ?? getObjectiveTitle()}
              </Typography>
            </Box>
          )}

          {/* SEARCH (only Assign Popup) */}
          {/* {isAssignPopup && (
          <TextField
            fullWidth
            placeholder={`Search ${assigneeRole === 'Assign Mentor' ? mentorRoleText : menteeRoleText}...`}
            value={searchValue}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#FDFDFD !important' }} />
                </InputAdornment>
              )
            }}
            sx={{
              background: '#1C1C1C !important',
              borderRadius: '6px !important',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#444444 !important'
                }
              }
            }}
          />
        )} */}

          {/* LIST TITLE */}
          {origin !== 'admin' && activeTab === 'recommendations' && (
            <Typography
              sx={{
                fontSize: '14px !important',
                fontWeight: '700 !important',
                color: '#FDFDFD !important'
              }}
            >
              {getListTitle()}
            </Typography>
          )}

          {/* TABS — only when AI Matching is enabled for this program */}
          {isAiMatchingEnabled && (
            <Box
              sx={{
                alignSelf: 'stretch',
                borderRadius: '6px',
                display: 'inline-flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  px: '16px',
                  py: '8px',
                  background:
                    activeTab === 'recommendations'
                      ? 'var(--Fill-Tab-tab-selected, #F79420)'
                      : 'var(--Fill-Tab-tab-default, #2A2A2A)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer'
                }}
                onClick={() => setActiveTab('recommendations')}
              >
                <Box
                  sx={{
                    color: activeTab === 'recommendations' ? '#FFFFFF' : '#B6B6B6',
                    fontSize: 12,
                    fontWeight: activeTab === 'recommendations' ? 700 : 400
                  }}
                >
                  Recommendations
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  px: '16px',
                  py: '8px',
                  background:
                    activeTab === 'ai'
                      ? 'var(--Fill-Tab-tab-selected, #F79420)'
                      : 'var(--Fill-Tab-tab-default, #2A2A2A)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer'
                }}
                onClick={() => setActiveTab('ai')}
              >
                <Box
                  sx={{
                    color: activeTab === 'ai' ? '#FFFFFF' : '#B6B6B6',
                    fontSize: 12,
                    fontWeight: activeTab === 'ai' ? 700 : 400,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                  Match with Elliott
                </Box>
              </Box>
            </Box>
          )}

          {/* RECOMMENDATIONS LIST */}
          {activeTab === 'recommendations' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {loading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    height: '160px'
                  }}
                >
                  <AppLoader />
                </Box>
              ) : combinedList().length > 0 ? (
                combinedList().map((mentor, index) => renderMentorRow(mentor, index))
              ) : (
                <Typography
                  sx={{
                    textAlign: 'center',
                    color: '#B6B6B6 !important',
                    py: '20px'
                  }}
                >
                  {getNoDataMessage()}
                </Typography>
              )}
            </Box>
          )}

          {/* AI SUGGESTIONS LIST */}
          {activeTab === 'ai' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  p: '12px',
                  borderRadius: '8px',
                  background: 'rgba(247, 148, 32, 0.08)',
                  border: '1px solid rgba(247, 148, 32, 0.2)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box sx={{ color: '#B6B6B6', fontSize: 12 }}>
                    AI ranks candidates by goals, background, and compatibility. {ALWAYS_ON_AI_FIELD_LABEL} is always considered.
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '2px' }}>
                  <input
                    id="ai-objectives-hard-filter-recruit"
                    type="checkbox"
                    checked={aiObjectivesHardFilter}
                    disabled={aiLoading}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setAiObjectivesHardFilter(next);
                      fetchAISuggestions({ objectivesHardFilter: next });
                    }}
                    style={{ accentColor: '#F79420', cursor: aiLoading ? 'not-allowed' : 'pointer' }}
                  />
                  <Box
                    component="label"
                    htmlFor="ai-objectives-hard-filter-recruit"
                    sx={{ color: '#FDFDFD', fontSize: 12, cursor: aiLoading ? 'not-allowed' : 'pointer' }}
                  >
                    Require shared learning objectives
                  </Box>
                </Box>
              </Box>

              {aiLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    minHeight: 220
                  }}
                >
                  <ElliottWorking message="Elliott is finding the best matches..." size="inline" />
                  <Box sx={{ color: '#B6B6B6', fontSize: 12, textAlign: 'center', maxWidth: 320 }}>
                    This can take up to a minute while Elliott reviews each candidate.
                  </Box>
                </Box>
              )}

              {!aiLoading && aiErrorMessage && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', py: 4 }}>
                  <Box sx={{ color: '#FF6B6B', fontSize: 13, textAlign: 'center' }}>{aiErrorMessage}</Box>
                  <Button
                    onClick={() => fetchAISuggestions()}
                    variant="outlined"
                    sx={{
                      background: '#1C1C1C',
                      borderColor: '#2A2A2A',
                      color: '#FDFDFD',
                      textTransform: 'none',
                      '&:hover': { borderColor: '#2A2A2A', background: '#262626' }
                    }}
                  >
                    Try Again
                  </Button>
                </Box>
              )}

              {!aiLoading && !aiErrorMessage && aiRecommendations.length === 0 && (
                <Typography sx={{ color: '#B6B6B6', fontSize: 14, textAlign: 'center', py: '20px' }}>
                  {aiEmptyMessage || 'No suggestions from Elliott available.'}
                </Typography>
              )}

              {!aiLoading && !aiErrorMessage && aiRecommendations.map((rec: any, idx: number) => {
                const name = rec.name || rec.displayName || 'Member';
                const avatar = rec.image || rec.headshot;
                const id = assigneeRoleForAI === 'Mentee' ? rec.mentorId : rec.menteeId;
                const score = typeof rec.aiScore === 'number' ? rec.aiScore : null;
                const scoreColor =
                  score !== null
                    ? (score >= 85 ? '#4CAF50' : score >= 70 ? '#F79420' : '#B6B6B6')
                    : '#B6B6B6';
                const isSelectedAI = id ? selectedIds.includes(id) : false;
                const cardKey = String(id ?? idx);
                const isExpanded = !!expandedAiCards[cardKey];
                return (
                  <Box
                    key={id || idx}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      p: '16px',
                      borderRadius: '8px',
                      border: '1px solid #2A2A2A',
                      background: '#1A1A1A'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <ChatAvatarComponent
                          image={avatar}
                          firstLetter={name.slice(0, 1)}
                          width={'40px'}
                          height={'40px'}
                          cursor="pointer"
                          type="noStatus"
                        />
                        {isAssignPopup && (
                          <Checkbox
                            checked={isSelectedAI}
                            onChange={() => handleAIMemberSelect(rec)}
                            sx={{ color: '#FDFDFD !important', p: 0 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 700 }}>{name}</Box>
                          {score !== null && (
                            <Box
                              sx={{
                                px: '8px',
                                py: '2px',
                                borderRadius: '20px',
                                border: `1px solid ${scoreColor}`,
                                background: `${scoreColor}14`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                              >
                              <Box sx={{ color: scoreColor, fontSize: 11, fontWeight: 600 }}>{score}%</Box>
                            </Box>
                          )}
                        </Box>
                        {(rec.aiOverallAssessment || rec.aiReasoning) && (
                          <Box sx={{ color: '#FDFDFD', fontSize: 13, lineHeight: 1.5, mt: 0.75 }}>
                            {rec.aiOverallAssessment || rec.aiReasoning}
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        {!isAssignPopup && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <IconButton
                              onClick={() => onChatClick(rec)}
                              sx={{
                                width: '36px !important',
                                height: '36px !important',
                                background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%) !important',
                                borderRadius: '999px !important'
                              }}
                            >
                              <QuestionAnswerIcon sx={{ color: 'white', fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              onClick={() => handleOpenProfile(rec)}
                              sx={{
                                width: '36px !important',
                                height: '36px !important',
                                background: '#CDCDCD !important',
                                borderRadius: '999px !important',
                                border: '1px solid #2A2A2A !important'
                              }}
                            >
                              <AccountCircleOutlinedIcon sx={{ color: 'black', fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        )}
                        {Array.isArray(rec.aiFieldComparisons) && rec.aiFieldComparisons.length > 0 && (
                          <Tooltip
                            title={isExpanded ? 'Hide field-by-field reasoning' : 'See field-by-field reasoning'}
                              placement="left"
                          >
                            <IconButton
                              onClick={() =>
                                setExpandedAiCards((prev) => ({ ...prev, [cardKey]: !prev[cardKey] }))
                              }
                              aria-label={isExpanded ? 'Hide field-by-field reasoning' : 'See field-by-field reasoning'}
                              sx={{
                                width: '36px !important',
                                height: '36px !important',
                                borderRadius: '999px !important',
                                border: `1px solid ${isExpanded ? '#F79420' : '#2A2A2A'} !important`,
                                background: isExpanded ? 'rgba(247,148,32,0.13)' : 'transparent'
                              }}
                            >
                              <AutoAwesomeIcon sx={{ fontSize: 18, color: '#F79420' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {isExpanded && Array.isArray(rec.aiFieldComparisons) && rec.aiFieldComparisons.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {rec.aiFieldComparisons.map((cmp: AIFieldComparison, cmpIdx: number) => (
                          <Box
                            key={`${cmp.field}-${cmpIdx}`}
                            sx={{ p: '10px', borderRadius: '6px', background: '#0F0F0F', border: '1px solid #2A2A2A' }}
                          >
                            <Box sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600, mb: '2px' }}>
                              {cmp.field}
                            </Box>
                            <Box sx={{ color: '#B6B6B6', fontSize: 12, lineHeight: 1.5 }}>
                              {cmp.reasoning}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        {/* FOOTER */}
        {isAssignPopup && (
          <DialogActions sx={{ p: '20px !important' }}>
            <Button
              onClick={handleSelect}
              variant="contained"
              fullWidth
              disabled={loading || selectedIds.length === 0}
              sx={{
                height: '48px !important',
                borderRadius: '6px !important',
                background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%) !important',
                textTransform: 'none !important',
                fontSize: '14px !important',
                fontWeight: '700 !important',
                color: 'white !important',
                '&.Mui-disabled': {
                  background: 'rgba(0, 0, 0, 0.2) !important',
                  color: '#565656 !important',
                  boxShadow: 'inset 0 0 0 1px #2A2A2A',
                  opacity: 1
                }
              }}
            >
              {loading
                ? origin === 'admin'
                  ? 'Assigning...'
                  : 'Selecting...'
                : origin === 'admin'
                ? 'Assign Match(es)'
                : 'Select'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <MenteeProfileModal
        open={profileModalOpen}
        onClose={handleCloseProfile}
        user={profileUser || {}}
        objectivesTitleOverride={apiType === 'mentee' ? 'Learning Objectives' : 'Areas of Expertise'}
      />
    </>
  );
};

export default RecruitNewMentor;
